import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Autocomplete,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Restaurant as RestaurantIcon,
  AutoAwesome as AutoAwesomeIcon,
  Flag as FlagIcon,
  ImageSearch as ImageSearchIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import api from "./api";
import countriesData from "./constants/countries.json";
import TagSelector from "./components/TagSelector";
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from "./utils/languages";

function AddRecipeForm() {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [servings, setServings] = useState(1);
  const [unitType, setUnitType] = useState("gram");
  const [unitValue, setUnitValue] = useState(100);
  const [countryOfOrigin, setCountryOfOrigin] = useState("");
  const [image, setImage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [foodSuggestions, setFoodSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [newFoodDialog, setNewFoodDialog] = useState(false);
  const [newFoodData, setNewFoodData] = useState({ name: '', category: '' });
  const [categories, setCategories] = useState([]);
  const [creatingFood, setCreatingFood] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [ingredientErrors, setIngredientErrors] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE);
  
  // Debounce refs
  const debounceTimeoutRef = useRef(null);

  // Helper function to get language display name
  const getLanguageDisplayName = (lang) => {
    const languageNames = {
      english: 'English',
      german: 'Deutsch',
      dutch: 'Nederlands',
      french: 'Français',
      hindi: 'हिन्दी',
      chinese: '中文',
      russian: 'Русский',
      japanese: '日本語',
      italian: 'Italiano',
      arabic: 'العربية',
      spanish: 'Español'
    };
    return languageNames[lang] || lang.charAt(0).toUpperCase() + lang.slice(1);
  };

  // Calculate total nutrition
  const calculateTotalNutrition = () => {
    const totals = ingredients.reduce(
      (acc, ing) => ({
        calories: acc.calories + (ing.calories || 0),
        protein: acc.protein + (ing.protein || 0),
        fat: acc.fat + (ing.fat || 0),
        carbs: acc.carbs + (ing.carbs || 0),
      }),
      { calories: 0, protein: 0, fat: 0, carbs: 0 }
    );
    return totals;
  };

  // Handle food search for ingredients with debounce
  const handleFoodSearch = useCallback((query, ingredientIndex) => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // If query is less than 3 characters, clear suggestions
    if (query.length < 3) {
      setFoodSuggestions([]);
      setSearchLoading(prev => ({ ...prev, [ingredientIndex]: false }));
      return;
    }

    // Set loading state for specific ingredient
    setSearchLoading(prev => ({ ...prev, [ingredientIndex]: true }));

    // Set new timeout for debounce (500ms delay)
    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await api.get("/foods/generic-autocomplete", {
          params: { 
            query, 
            language: selectedLanguage 
          },
        });
        setFoodSuggestions(response.data.data || []);
      } catch (err) {
        console.error("Error searching foods:", err);
        setFoodSuggestions([]);
      } finally {
        setSearchLoading(prev => ({ ...prev, [ingredientIndex]: false }));
      }
    }, 500);
  }, [selectedLanguage]);

  // Handle selecting a food for ingredient
  const handleSelectFood = async (food, index) => {
    // If food is null (cross button clicked), clear the ingredient
    if (!food) {
      const newIngredients = [...ingredients];
      newIngredients[index] = {
        ...newIngredients[index], // Preserve existing properties including ID
        foodId: null,
        name: "",
        amount: 100,
        unit: "g",
        original: "",
        calories: 0,
        protein: 0,
        fat: 0,
        carbs: 0,
        glycemicIndex: 0,
        basePer100g: {
          calories: 0,
          protein: 0,
          fat: 0,
          carbs: 0,
        },
      };
      setIngredients(newIngredients);
      
      // Clear any errors for this ingredient
      setIngredientErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      return;
    }

    try {
      const response = await api.get(`/foods/food/${food.id}`, {
        params: { language: selectedLanguage }
      });

      if (response.data.success) {
        const foodData = response.data.data;
        
        // Clear any errors for this ingredient
        setIngredientErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
        
        const newIngredients = [...ingredients];
        newIngredients[index] = {
          ...newIngredients[index], // Preserve existing properties including ID
          foodId: foodData.id,
          name: foodData.name,
          amount: foodData.unitValue,
          unit: foodData.unitType,
          original: `${foodData.unitValue} ${foodData.unitType} ${foodData.name}`,
          calories: parseFloat(foodData.calories) || 0,
          protein: parseFloat(foodData.protein) || 0,
          fat: parseFloat(foodData.fat) || 0,
          carbs: parseFloat(foodData.carbs) || 0,
          glycemicIndex: parseFloat(foodData.glycemicIndex) || 0,
          basePer100g: {
            calories: parseFloat(foodData.calories) || 0,
            protein: parseFloat(foodData.protein) || 0,
            fat: parseFloat(foodData.fat) || 0,
            carbs: parseFloat(foodData.carbs) || 0,
          },
        };
        setIngredients(newIngredients);
      }
    } catch (err) {
      console.error("Error fetching food details:", err);
    }
  };

  // Handle adding new ingredient
  const handleAddIngredient = () => {
    const uniqueId = Date.now() + Math.random(); // Create unique ID
    setIngredients([
      ...ingredients,
      {
        id: uniqueId,
        name: "",
        amount: 100,
        unit: "g",
        calories: 0,
        protein: 0,
        fat: 0,
        carbs: 0,
        glycemicIndex: 0,
      },
    ]);
  };

  // Handle removing ingredient
  const handleRemoveIngredient = (index) => {
    const newIngredients = [...ingredients];
    newIngredients.splice(index, 1);
    setIngredients(newIngredients);
    
    // Remove errors for this ingredient and reindex remaining errors
    setIngredientErrors(prev => {
      const newErrors = {};
      Object.keys(prev).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex < index) {
          newErrors[key] = prev[key];
        } else if (keyIndex > index) {
          newErrors[keyIndex - 1] = prev[key];
        }
      });
      return newErrors;
    });
  };

  // Handle updating ingredient amount
  const handleUpdateIngredientAmount = (index, amount) => {
    const newIngredients = [...ingredients];
    const ingredient = newIngredients[index];
    const numericAmount = parseFloat(amount) || 0;

    ingredient.amount = numericAmount;

    if (ingredient.foodId && numericAmount > 0 && ingredient.basePer100g) {
      const factor = numericAmount / 100;
      ingredient.calories = ingredient.basePer100g.calories * factor;
      ingredient.protein = ingredient.basePer100g.protein * factor;
      ingredient.fat = ingredient.basePer100g.fat * factor;
      ingredient.carbs = ingredient.basePer100g.carbs * factor;
      ingredient.original = `${numericAmount} ${ingredient.unit} ${ingredient.name}`;
    }

    setIngredients(newIngredients);
  };

  // Fetch categories when component mounts
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/category/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Handle creating new food
  const handleCreateNewFood = async () => {
    if (!newFoodData.name.trim() || !newFoodData.category) {
      setError("Please enter food name and select category");
      return;
    }

    setCreatingFood(true);
    try {
      // First, try to get AI nutritional data
      const aiResponse = await api.post('/foods/fill-with-ai', {
        name: newFoodData.name,
        category: newFoodData.category,
        generateImage: true,
        languages: [selectedLanguage]
      });

      let foodData = {
        name: newFoodData.name,
        category: newFoodData.category,
        isCustom: true,
        calories: 0,
        protein: 0,
        fat: 0,
        carbs: 0,
        glycemicIndex: 50,
        languageSpec: {
          [selectedLanguage]: { name: newFoodData.name, customImageUrl: '' }
        }
      };

      // If AI data is available, use it
      if (aiResponse.data.success) {
        const aiData = aiResponse.data.data;
        foodData = {
          ...foodData,
          calories: aiData.calories || 0,
          protein: aiData.protein || 0,
          fat: aiData.fat || 0,
          carbs: aiData.carbs || 0,
          glycemicIndex: aiData.glycemicIndex || 50,
          image: aiData.generatedImage || '',
          languageSpec: {
            ...foodData.languageSpec,
            ...aiData.languageSpec
          }
        };
      }

      // Create the food item
      const createResponse = await api.post('/foods', foodData);
      
      if (createResponse.data.success) {
        const createdFood = createResponse.data.data;
        
        // Add as ingredient to current recipe
        const newIngredients = [...ingredients];
        const uniqueId = Date.now() + Math.random();
        newIngredients.push({
          id: uniqueId,
          foodId: createdFood.id,
          name: createdFood.name,
          amount: 100,
          unit: "g",
          original: `100 g ${createdFood.name}`,
          calories: parseFloat(createdFood.calories) || 0,
          protein: parseFloat(createdFood.protein) || 0,
          fat: parseFloat(createdFood.fat) || 0,
          carbs: parseFloat(createdFood.carbs) || 0,
          glycemicIndex: parseFloat(createdFood.glycemicIndex) || 0,
          basePer100g: {
            calories: parseFloat(createdFood.calories) || 0,
            protein: parseFloat(createdFood.protein) || 0,
            fat: parseFloat(createdFood.fat) || 0,
            carbs: parseFloat(createdFood.carbs) || 0,
          },
        });
        setIngredients(newIngredients);
        
        setSuccess(`Food "${createdFood.name}" created and added to recipe!`);
        setNewFoodDialog(false);
        setNewFoodData({ name: '', category: '' });
      }
    } catch (error) {
      console.error('Failed to create food:', error);
      setError('Failed to create food item. Please try again.');
    } finally {
      setCreatingFood(false);
    }
  };

  // Handle AI image generation
  const handleGenerateImage = async () => {
    if (!title.trim()) {
      setError("Please enter a recipe title to generate an image");
      return;
    }

    setGeneratingImage(true);
    setError("");
    setSuccess("");

    try {
      const response = await api.post("/foods/generate-image", {
        name: title,
        category: null,
        customPrompt: `recipe: ${title}${summary ? `, ${summary}` : ''}, professional food photography style`
      });

      // Check if response exists and has expected structure
      if (response && response.data) {
        if (response.data.success && response.data.data && response.data.data.imageUrl) {
          setImageUrl(response.data.data.imageUrl);
          setSuccess("AI image generated successfully!");
        } else {
          // Handle API success=false or missing data
          const errorMessage = response.data.error || response.data.message || "Failed to generate AI image. Please try again.";
          setError(errorMessage);
        }
      } else {
        setError("Invalid response from server. Please try again.");
      }
    } catch (err) {
      console.error("AI image generation error:", err);
      
      // Comprehensive error handling
      let errorMessage = "Failed to generate AI image. Please try again.";
      
      if (err?.response) {
        // Server responded with error status
        if (err.response.status === 401) {
          errorMessage = "Authentication required. Please log in again.";
        } else if (err.response.status === 403) {
          errorMessage = "You don't have permission to generate images.";
        } else if (err.response.status === 429) {
          errorMessage = "Too many requests. Please wait a moment and try again.";
        } else if (err.response.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (err.response.data) {
          // Try to extract error message from response
          errorMessage = err.response.data.error || 
                        err.response.data.message || 
                        err.response.data.details || 
                        `Server error (${err.response.status}). Please try again.`;
        }
      } else if (err?.request) {
        // Network error
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (err?.message) {
        // Other errors
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setGeneratingImage(false);
    }
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await api.post("/upload-image/public-admin/upload-recipe-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        setImageUrl(response.data.url);
        setSuccess("Image uploaded successfully");
      }
    } catch (err) {
      console.log("🤔 ~ :194 ~ handleImageUpload ~ err:", err)
      // Fallback to base64 if upload fails
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
      setError("Failed to upload image to server, using local storage");
    } finally {
      setUploadingImage(false);
    }
  };

  // Validate ingredients
  const validateIngredients = () => {
    const errors = {};
    let hasValidIngredient = false;
    
    ingredients.forEach((ingredient, index) => {
      const ingredientErrors = [];
      
      if (!ingredient.name || ingredient.name.trim() === '') {
        ingredientErrors.push('Ingredient name is required');
      }
      
      if (!ingredient.foodId) {
        ingredientErrors.push('Please select a valid food item');
      }
      
      if (ingredientErrors.length > 0) {
        errors[index] = ingredientErrors;
      } else {
        hasValidIngredient = true;
      }
    });
    
    setIngredientErrors(errors);
    return { isValid: Object.keys(errors).length === 0, hasValidIngredient };
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!title || ingredients.length === 0 || !countryOfOrigin) {
      setError("Please fill in recipe title, select country of origin, and add at least one ingredient");
      return;
    }

    // Validate ingredients
    const { isValid, hasValidIngredient } = validateIngredients();
    
    if (!isValid) {
      setError("Please fix ingredient errors. Each ingredient must have a name and be selected from the search.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    const nutrition = calculateTotalNutrition();
    const endpoint = "/recipes/public-admin/create-global-recipe";

    try {
      const recipeData = {
        title,
        summary,
        countryOfOrigin,
        image: image || null,
        imageUrl: imageUrl || null,
        servings: parseInt(servings) || 1,
        unitType: unitType,
        unitValue: parseFloat(unitValue) || 100,
        extendedIngredients: ingredients.map((ingredient) => ({
            foodId: ingredient.foodId || null,
            name: ingredient.name,
            amount: ingredient.amount,
            unit: ingredient.unit,
            original:
              ingredient.original ||
              `${ingredient.amount} ${ingredient.unit} ${ingredient.name}`.trim(),
            calories: ingredient.calories || 0,
            protein: ingredient.protein || 0,
            fat: ingredient.fat || 0,
            carbs: ingredient.carbs || 0,
            glycemicIndex: ingredient.glycemicIndex || 0,
          })),
        nutrition: {
          nutrients: [
            { name: "Calories", amount: nutrition.calories },
            { name: "Protein", amount: nutrition.protein },
            { name: "Fat", amount: nutrition.fat },
            { name: "Carbohydrates", amount: nutrition.carbs },
          ],
        },
      };

      const response = await api.post(endpoint, recipeData);

      if (response.data.success) {
        const createdRecipe = response.data.data;
        
        // Assign tags to the recipe if any are selected
        if (selectedTagIds.length > 0) {
          try {
            await api.post(`/tags/public-admin/recipe/${createdRecipe.id}`, {
              tagIds: selectedTagIds
            });
          } catch (tagError) {
            console.error('Error assigning tags to recipe:', tagError);
            // Don't fail the whole operation for tag assignment errors
          }
        }
        
        setSuccess("Global recipe created successfully! Translations are being generated in the background.");
        // Reset form
        setTitle("");
        setSummary("");
        setServings(1);
        setCountryOfOrigin("");
        setIngredients([]);
        setImage("");
        setImageUrl("");
        setSelectedTagIds([]);
        setSelectedLanguage(DEFAULT_LANGUAGE);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create recipe");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear messages after 5 seconds
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const nutrition = calculateTotalNutrition();

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
        <RestaurantIcon />
        Add Global Recipe
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Language Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageIcon />
          Recipe Language
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {AVAILABLE_LANGUAGES.map(lang => (
            <Chip
              key={lang}
              label={getLanguageDisplayName(lang)}
              onClick={() => setSelectedLanguage(lang)}
              color={selectedLanguage === lang ? 'primary' : 'default'}
              variant={selectedLanguage === lang ? 'filled' : 'outlined'}
              size="medium"
              sx={{
                fontWeight: selectedLanguage === lang ? 'bold' : 'normal',
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s'
                }
              }}
            />
          ))}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Select the language for ingredient search and recipe content.
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recipe Information
              </Typography>
              <TextField
                fullWidth
                label="Recipe Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Servings"
                type="number"
                value={servings}
                onChange={(e) => setServings(e.target.value)}
                InputProps={{ inputProps: { min: 1 } }}
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Unit Type</InputLabel>
                <Select
                  value={unitType}
                  label="Unit Type"
                  onChange={(e) => setUnitType(e.target.value)}
                >
                  <MenuItem value="gram">Gram</MenuItem>
                  <MenuItem value="ml">Milliliter</MenuItem>
                  <MenuItem value="piece_portion">Piece/Portion</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Unit Value"
                type="number"
                value={unitValue}
                onChange={(e) => setUnitValue(e.target.value)}
                InputProps={{ inputProps: { min: 1 } }}
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth sx={{ mb: 2 }} required>
                <InputLabel>Country of Origin</InputLabel>
                <Select
                  value={countryOfOrigin}
                  label="Country of Origin"
                  onChange={(e) => setCountryOfOrigin(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 250,
                        overflow: 'auto',
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) return '';
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={`/images/country/${selected.toLowerCase()}.svg`}
                          sx={{ width: 24, height: 24 }}
                        >
                          <FlagIcon fontSize="small" />
                        </Avatar>
                        {countriesData[selected]}
                      </Box>
                    );
                  }}
                >
                  {Object.entries(countriesData).map(([code, name]) => (
                    <MenuItem key={code} value={code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={`/images/country/${code.toLowerCase()}.svg`}
                          sx={{ width: 24, height: 24 }}
                        >
                          <FlagIcon fontSize="small" />
                        </Avatar>
                        {name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This recipe will be available to all users globally. Select the language for ingredient search and recipe content.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Image Upload */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recipe Image
              </Typography>
              <Box sx={{ textAlign: "center" }}>
                {(imageUrl || image) && (
                  <Box sx={{ mb: 2 }}>
                    <img
                      src={imageUrl || image}
                      alt="Recipe"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 200,
                        borderRadius: 8,
                      }}
                    />
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={uploadingImage ? <CircularProgress size={20} /> : <UploadIcon />}
                    disabled={uploadingImage || generatingImage}
                  >
                    {uploadingImage ? "Uploading..." : "Upload Image"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={generatingImage ? <CircularProgress size={20} /> : <ImageSearchIcon />}
                    onClick={handleGenerateImage}
                    disabled={generatingImage || uploadingImage || !title.trim()}
                    color="secondary"
                  >
                    {generatingImage ? "Generating..." : "AI Generate"}
                  </Button>
                </Box>
                {!title.trim() && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Enter a recipe title to enable AI image generation
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tags */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recipe Tags
              </Typography>
              <TagSelector
                selectedTagIds={selectedTagIds}
                onTagsChange={setSelectedTagIds}
                label="Select tags for this recipe"
                placeholder="Choose relevant tags to categorize this recipe..."
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Tags help users find and categorize recipes easily.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Ingredients */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">
                  Ingredients
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => setNewFoodDialog(true)}
                    variant="outlined"
                    size="small"
                    color="secondary"
                  >
                    Create New Food
                  </Button>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddIngredient}
                    variant="contained"
                    size="small"
                  >
                    Add Ingredient
                  </Button>
                </Box>
              </Box>

              {ingredients.map((ingredient, index) => (
                <Paper 
                  key={ingredient.id || index} 
                  sx={{ 
                    p: 2, 
                    mb: 2,
                    border: ingredientErrors[index] ? '2px solid' : 'none',
                    borderColor: 'error.main'
                  }} 
                  elevation={2}
                >
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        options={foodSuggestions}
                        value={ingredient.foodId ? { name: ingredient.name, id: ingredient.foodId } : null}
                        getOptionLabel={(option) => option.word || option.name || ""}
                        loading={searchLoading[index] || false}
                        onInputChange={(event, value) => handleFoodSearch(value, index)}
                        onChange={(event, value) => handleSelectFood(value, index)}
                        noOptionsText={
                          searchLoading[index] 
                            ? "Searching..." 
                            : "Type at least 3 characters to search foods"
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search Food"
                            variant="outlined"
                            error={!!ingredientErrors[index]}
                            helperText={
                              ingredientErrors[index] 
                                ? ingredientErrors[index].join('. ') 
                                : "Type at least 3 characters to search"
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {searchLoading[index] ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={ingredient.amount}
                        onChange={(e) => handleUpdateIngredientAmount(index, e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{ingredient.unit}</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} md={5}>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Chip label={`${ingredient.calories.toFixed(1)} cal`} size="small" />
                        <Chip label={`${ingredient.protein.toFixed(1)}g protein`} size="small" />
                        <Chip label={`${ingredient.fat.toFixed(1)}g fat`} size="small" />
                        <Chip label={`${ingredient.carbs.toFixed(1)}g carbs`} size="small" />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveIngredient(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}

              {ingredients.length === 0 && (
                <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                  No ingredients added yet. Click "Add Ingredient" to start.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Total Nutrition */}
        {ingredients.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total Nutrition (per recipe)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center", bgcolor: "primary.light", color: "primary.contrastText" }}>
                      <Typography variant="h4">{nutrition.calories.toFixed(0)}</Typography>
                      <Typography>Calories</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center", bgcolor: "secondary.light", color: "secondary.contrastText" }}>
                      <Typography variant="h4">{nutrition.protein.toFixed(1)}g</Typography>
                      <Typography>Protein</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center", bgcolor: "warning.light", color: "warning.contrastText" }}>
                      <Typography variant="h4">{nutrition.fat.toFixed(1)}g</Typography>
                      <Typography>Fat</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: "center", bgcolor: "info.light", color: "info.contrastText" }}>
                      <Typography variant="h4">{nutrition.carbs.toFixed(1)}g</Typography>
                      <Typography>Carbs</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Submit Button */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={loading}
              sx={{ minWidth: 200 }}
            >
              {loading ? <CircularProgress size={24} /> : "Save Global Recipe"}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Create New Food Dialog */}
      <Dialog open={newFoodDialog} onClose={() => setNewFoodDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon color="secondary" />
            Create New Food Item
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a new food item that doesn't exist in the database. AI will help fetch nutritional information.
          </Typography>
          
          <TextField
            fullWidth
            label="Food Name"
            value={newFoodData.name}
            onChange={(e) => setNewFoodData({ ...newFoodData, name: e.target.value })}
            sx={{ mb: 3 }}
            placeholder="e.g., Organic Quinoa, Fresh Salmon, etc."
          />
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={newFoodData.category}
              label="Category"
              onChange={(e) => setNewFoodData({ ...newFoodData, category: e.target.value })}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.name}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>AI Enhancement:</strong> After creation, the system will automatically fetch nutritional information 
            and generate an appropriate food image using AI technology.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFoodDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateNewFood}
            variant="contained"
            disabled={creatingFood || !newFoodData.name.trim() || !newFoodData.category}
            startIcon={creatingFood ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
          >
            {creatingFood ? 'Creating with AI...' : 'Create & Add to Recipe'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AddRecipeForm;